import React, { useEffect, useState, useRef } from "react";
import { api } from "../../../lib/api";
import {
  Maximize2, Download, Upload, Trash2, Loader2, FileText, X,
  Eye, EyeOff, AlertCircle, CheckCircle2,
} from "lucide-react";

/**
 * PosterSection — dedicated "Locandina" management for the EditorV2.
 *
 * Displays the poster thumbnail + metadata + actions
 *   • Anteprima cliccabile → fullscreen modal
 *   • Scarica            → POST download endpoint (safe filename)
 *   • Sostituisci        → replaces the original file + regenerates thumb
 *   • Rimuovi            → clears poster_* fields
 *   • ☑ Mostra sul pubblico → toggles poster_is_public (server side)
 *
 * Contract with EditorV2:
 *   - `value` is a "poster" sub-object built from event columns:
 *       { url, thumbnail_url, filename, mime_type, size_bytes, uploaded_at, is_public }
 *   - `onChange(patch)` mutates the parent's local event state (patch of
 *     poster_* columns using the DB column names — ready to be included in
 *     the next PATCH /admin/events/{id} save).
 *   - When `eventId` is set, replace/remove/togglePublic hit the server
 *     immediately; otherwise (new event before first save) we just mutate
 *     local state.
 */

const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};

const isPdf = (mime, url) => (mime === "application/pdf") || /\.pdf(\?|$)/i.test(url || "");

// ---------------------------------------------------------------------------
// Fullscreen viewer (images only)
// ---------------------------------------------------------------------------
const FullscreenViewer = ({ open, src, alt, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="poster-fullscreen"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2"
        aria-label="Chiudi anteprima"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={alt || "Locandina"}
        className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Empty state (no poster yet)
// ---------------------------------------------------------------------------
const EmptyPoster = ({ onFile, uploading, disabled, error }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const accept = (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png|webp|pdf)$/i.test(f.name)) return; // parent shows the toast
    onFile(f);
  };

  return (
    <>
      <label
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) accept(e.dataTransfer?.files?.[0]); }}
        className={`block cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition ${
          disabled ? "opacity-60 cursor-not-allowed border-slate-200" :
          dragging ? "border-emerald-500 bg-emerald-50/40" :
          "border-slate-200 hover:border-emerald-400 hover:bg-slate-50/50"
        }`}
        data-testid="poster-upload-empty"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          onChange={(e) => accept(e.target.files?.[0])}
          className="hidden"
          disabled={disabled || uploading}
          data-testid="poster-upload-input"
        />
        {uploading ? (
          <span className="inline-flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento locandina…
          </span>
        ) : (
          <>
            <div className="text-sm font-medium text-slate-700">
              Trascina la locandina o clicca per caricare
            </div>
            <div className="mt-1 text-xs text-slate-400">JPG · PNG · WEBP · PDF — max 20 MB</div>
          </>
        )}
      </label>
      {error && (
        <div className="mt-3 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Populated state
// ---------------------------------------------------------------------------
const PopulatedPoster = ({
  value, eventId, onChange, onError, onSuccess,
}) => {
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [busy, setBusy] = useState(null); // 'replace' | 'remove' | 'toggle' | null
  const inputRef = useRef(null);
  const pdf = isPdf(value.mime_type, value.url);
  const thumb = value.thumbnail_url || (!pdf ? value.url : null);
  const downloadEndpoint = eventId ? `/api/events/${eventId}/poster/download` : null;

  const replaceFile = async (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png|webp|pdf)$/i.test(f.name)) {
      onError?.("Formato non supportato. Ammessi: JPG, PNG, WEBP, PDF.");
      return;
    }
    if (!eventId) {
      // For new events: defer to parent — it will re-run its upload pipeline via onChange({__file: f})
      onChange?.({ __localFile: f });
      return;
    }
    setBusy("replace");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/events/${eventId}/poster`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.({
        poster_url: r.data.poster_url,
        poster_thumbnail_url: r.data.poster_thumbnail_url,
        poster_original_filename: r.data.poster_original_filename,
        poster_mime_type: r.data.poster_mime_type,
        poster_size_bytes: r.data.poster_size_bytes,
        poster_uploaded_at: r.data.poster_uploaded_at,
      });
      onSuccess?.("Locandina sostituita");
    } catch (e) {
      onError?.(e?.response?.data?.detail || "Errore durante la sostituzione");
    } finally { setBusy(null); }
  };

  const removePoster = async () => {
    if (!window.confirm("Rimuovere la locandina di questo evento? Il file resterà nello storage.")) return;
    if (!eventId) {
      onChange?.({
        poster_url: null, poster_thumbnail_url: null, poster_original_filename: null,
        poster_mime_type: null, poster_size_bytes: null, poster_uploaded_at: null,
        poster_is_public: false,
      });
      return;
    }
    setBusy("remove");
    try {
      await api.delete(`/admin/events/${eventId}/poster`);
      onChange?.({
        poster_url: null, poster_thumbnail_url: null, poster_original_filename: null,
        poster_mime_type: null, poster_size_bytes: null, poster_uploaded_at: null,
        poster_is_public: false,
      });
      onSuccess?.("Locandina rimossa");
    } catch (e) {
      onError?.(e?.response?.data?.detail || "Errore durante la rimozione");
    } finally { setBusy(null); }
  };

  const togglePublic = async (next) => {
    if (!eventId) {
      onChange?.({ poster_is_public: next });
      return;
    }
    setBusy("toggle");
    try {
      await api.patch(`/admin/events/${eventId}/poster`, { poster_is_public: next });
      onChange?.({ poster_is_public: next });
      onSuccess?.(next ? "Locandina ora visibile sul sito" : "Locandina nascosta al pubblico");
    } catch (e) {
      onError?.(e?.response?.data?.detail || "Errore aggiornamento visibilità");
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4">
        {/* Preview */}
        <div className="w-full max-w-[160px] mx-auto sm:mx-0">
          {thumb ? (
            pdf ? (
              <a
                href={value.url}
                target="_blank"
                rel="noreferrer"
                className="block relative group"
                data-testid="poster-open-pdf"
              >
                <img
                  src={thumb}
                  alt="Anteprima locandina"
                  className="w-full aspect-[3/4] object-cover rounded-lg border border-slate-200 shadow-sm"
                />
                <span className="absolute bottom-1 left-1 right-1 rounded-md bg-rose-600 text-white text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 text-center shadow">
                  PDF
                </span>
              </a>
            ) : (
              <button
                type="button"
                onClick={() => setFullscreenOpen(true)}
                className="w-full block relative group"
                data-testid="poster-open-fullscreen"
              >
                <img
                  src={thumb}
                  alt="Anteprima locandina"
                  className="w-full aspect-[3/4] object-cover rounded-lg border border-slate-200 shadow-sm group-hover:shadow-md transition"
                />
                <span className="absolute inset-0 rounded-lg bg-slate-900/0 group-hover:bg-slate-900/25 transition flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                  <Maximize2 className="w-5 h-5" />
                </span>
              </button>
            )
          ) : (
            <div className="w-full aspect-[3/4] rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
              <FileText className="w-8 h-8" />
            </div>
          )}
        </div>

        {/* Actions + metadata */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="text-xs text-slate-500 leading-relaxed">
            <div className="text-slate-800 font-medium truncate">
              {value.filename || "locandina"}
            </div>
            <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px]">
              {value.size_bytes ? <span>{formatBytes(value.size_bytes)}</span> : null}
              {value.mime_type ? <span className="uppercase">{value.mime_type.split("/")[1]}</span> : null}
              {value.uploaded_at ? <span>caricata il {formatDate(value.uploaded_at)}</span> : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {pdf ? (
              <a
                href={value.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="poster-open-pdf-btn"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Apri PDF
              </a>
            ) : (
              <button
                type="button"
                onClick={() => setFullscreenOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="poster-open-fullscreen-btn"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Schermo intero
              </button>
            )}
            {downloadEndpoint && value.is_public && (
              <a
                href={downloadEndpoint}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="poster-download-btn"
              >
                <Download className="w-3.5 h-3.5" /> Scarica
              </a>
            )}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={!!busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              data-testid="poster-replace-btn"
            >
              {busy === "replace"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Upload className="w-3.5 h-3.5" />}
              Sostituisci
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              onChange={(e) => replaceFile(e.target.files?.[0])}
              className="hidden"
              data-testid="poster-replace-input"
            />
            <button
              type="button"
              onClick={removePoster}
              disabled={!!busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              data-testid="poster-remove-btn"
            >
              {busy === "remove"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Trash2 className="w-3.5 h-3.5" />}
              Rimuovi
            </button>
          </div>

          {/* Public toggle */}
          <label
            className={`mt-1 flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition ${
              value.is_public
                ? "border-emerald-200 bg-emerald-50/40"
                : "border-slate-200 bg-slate-50"
            }`}
            data-testid="poster-public-toggle"
          >
            <input
              type="checkbox"
              checked={!!value.is_public}
              onChange={(e) => togglePublic(e.target.checked)}
              disabled={busy === "toggle"}
              className="mt-0.5 accent-emerald-600"
              data-testid="poster-public-checkbox"
            />
            <div className="flex-1 text-xs">
              <div className="font-medium text-slate-800 inline-flex items-center gap-1.5">
                {value.is_public ? (
                  <><Eye className="w-3.5 h-3.5 text-emerald-700" /> Mostra la locandina nella pagina pubblica</>
                ) : (
                  <><EyeOff className="w-3.5 h-3.5 text-slate-500" /> Locandina non pubblica</>
                )}
              </div>
              <div className="mt-0.5 text-slate-500 leading-tight">
                {value.is_public
                  ? "Sarà visibile in una sezione dedicata sulla pagina evento, con download disponibile."
                  : "Resta nell'archivio admin — nessun visitatore può vederla o scaricarla."}
              </div>
            </div>
            {busy === "toggle" && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
          </label>
        </div>
      </div>

      <FullscreenViewer
        open={fullscreenOpen}
        src={value.url}
        alt={value.filename || "Locandina"}
        onClose={() => setFullscreenOpen(false)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
export default function PosterSection({ value, eventId, onChange, onError, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const hasPoster = !!(value && value.url);

  const uploadInitial = async (f) => {
    setError(null);
    if (!eventId) {
      // New event — defer: parent needs to save the event first, OR
      // handle this via the AI-import path. For now we ask the user to
      // save the event before adding a manual poster.
      setError("Salva prima l'evento (Salva bozza), poi carica la locandina.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/events/${eventId}/poster`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.({
        poster_url: r.data.poster_url,
        poster_thumbnail_url: r.data.poster_thumbnail_url,
        poster_original_filename: r.data.poster_original_filename,
        poster_mime_type: r.data.poster_mime_type,
        poster_size_bytes: r.data.poster_size_bytes,
        poster_uploaded_at: r.data.poster_uploaded_at,
        poster_is_public: false,   // default off for manual uploads (spec)
      });
      onSuccess?.("Locandina caricata");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore durante l'upload";
      setError(msg);
      onError?.(msg);
    } finally { setUploading(false); }
  };

  if (!hasPoster) {
    return <EmptyPoster onFile={uploadInitial} uploading={uploading} error={error} disabled={false} />;
  }
  return (
    <PopulatedPoster
      value={value}
      eventId={eventId}
      onChange={onChange}
      onError={(m) => { setError(m); onError?.(m); }}
      onSuccess={onSuccess}
    />
  );
}
